import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTenant } from '../contexts/TenantContext';
import { branding } from '../config/branding';
import { cn } from '../lib/utils';
import { resolveSplashLogo } from '../lib/tenantBranding';

interface AppSplashProps {
  className?: string;
  /** Use platform logo even on tenant sites (rare). */
  forcePlatform?: boolean;
}

/**
 * Full-screen loading splash: pulsing logo + progress bar.
 * On tenant app/site routes uses the organization logo when available.
 */
const AppSplash: React.FC<AppSplashProps> = ({ className, forcePlatform = false }) => {
  const { tenant } = useTenant();
  const { pathname } = useLocation();
  const [logoError, setLogoError] = useState(false);

  const { logoUrl, tenantName, isTenantContext } = resolveSplashLogo(tenant, pathname);
  const useTenantBranding = !forcePlatform && isTenantContext;
  const showTenantLogo = useTenantBranding && logoUrl && !logoError;
  const showPlatformLogo = !useTenantBranding || (!showTenantLogo && !tenantName);

  return (
    <div
      className={cn(
        'min-h-screen flex flex-col items-center justify-center gap-3',
        useTenantBranding ? 'bg-white' : 'bg-gray-50',
        className
      )}
    >
      {showTenantLogo ? (
        <img
          src={logoUrl!}
          alt={tenantName || ''}
          className="h-10 w-auto max-w-[200px] object-contain animate-pulse"
          onError={() => setLogoError(true)}
        />
      ) : useTenantBranding && tenantName ? (
        <div className="h-10 min-w-[2.5rem] px-2 flex items-center justify-center bg-gray-100 rounded-lg border border-gray-200 animate-pulse">
          <span className="text-gray-500 font-bold text-xs uppercase tracking-wide">
            {tenantName.substring(0, 2)}
          </span>
        </div>
      ) : showPlatformLogo ? (
        <img
          src={branding.logos.main}
          alt=""
          className="h-10 w-auto animate-pulse"
        />
      ) : null}

      <div className="h-1 w-24 rounded-full bg-gray-200 overflow-hidden">
        <div className="h-full w-1/3 rounded-full bg-gray-900 animate-splash-bar" />
      </div>
    </div>
  );
};

export default AppSplash;
